import type { ReactNode } from "react";
import "./PageBody.css";

/**
 * The other page shape: a document rather than a workspace.
 *
 * Astrum Connect has two kinds of screen, and conflating them is what made the
 * layout look inconsistent in the first place:
 *
 * - **Operating surfaces** — Chat, Work, the detail views. A fixed-height
 *   sticky bar of controls over a scrolling region. That is `PageHeader`.
 * - **Documents** — Home, Administration, Profile. Something you read and
 *   occasionally act on, set in a centred column with a large title that
 *   scrolls away with the content. That is this.
 *
 * Home, Admin and Profile each wrote this out by hand and had already drifted:
 * three column widths, and Profile's title two steps smaller than the other
 * two. The width is deliberately still a choice — a settings page and a
 * dashboard genuinely want different measures — but it is now a named choice
 * from a fixed set rather than whatever the last author typed.
 */

export type PageBodyWidth = "narrow" | "regular" | "wide";

export interface PageBodyProps {
  /** The page title. Always rendered as the h1. */
  title: ReactNode;
  /** Small uppercase line above the title — a date, a section name. */
  eyebrow?: ReactNode;
  /** One line under the title saying what the page is for. */
  subtitle?: ReactNode;
  /** Controls aligned right of the title, e.g. a primary action. */
  actions?: ReactNode;
  /**
   * Replaces the title block entirely — for a page whose heading is a
   * composition rather than text, like Profile's avatar and identity. Set this
   * and `title`/`eyebrow`/`subtitle` are ignored.
   */
  heading?: ReactNode;
  /**
   * Column measure. `narrow` (640px) for a single column of settings,
   * `regular` (980px) for a page of cards, `wide` (1080px) for a dashboard.
   */
  width?: PageBodyWidth;
  className?: string;
  children: ReactNode;
}

export function PageBody({
  title,
  eyebrow,
  subtitle,
  actions,
  heading,
  width = "regular",
  className,
  children,
}: PageBodyProps) {
  return (
    <div className={["ui-page-body", className ?? ""].filter(Boolean).join(" ")}>
      <div className={`ui-page-body__inner ui-page-body__inner--${width}`}>
        <header className="ui-page-body__header">
          {heading ?? (
            <div className="ui-page-body__titles">
              {eyebrow && <span className="ui-page-body__eyebrow">{eyebrow}</span>}
              {/* Always an h1, so the document outline is right without each
                  caller having to remember. */}
              <h1 className="ui-page-body__title">{title}</h1>
              {subtitle && <p className="ui-page-body__subtitle">{subtitle}</p>}
            </div>
          )}

          {actions && <div className="ui-page-body__actions">{actions}</div>}
        </header>

        {children}
      </div>
    </div>
  );
}
